import React, { useEffect, useMemo, useRef, useState } from "react";
import { Alert, Box, Button, Dialog, IconButton, ToggleButton, ToggleButtonGroup, Typography, useMediaQuery } from "@mui/material";
import { useTheme } from "@mui/material/styles";
import { Close } from "@mui/icons-material";
import PlanoCompra from "./PlanoCompra";
import PanelMesa, { Cantidad } from "./PanelMesa";
import { useMapaCompra } from "./MapaCompra";
import {
  MESA_ENTERA,
  aCambios,
  alternarAsiento,
  asientosDeTarifa,
  conLugares,
  desdeSeleccion,
  mesaLibre,
  paquetesQueEntran,
  ponerMesa,
  quitarMesa,
  resumen,
  tarifaMesaEntera,
  tarifasPorSilla,
  esGeneralEnPlano,
  tarifasGenerales,
  ponerGeneral,
  cantidadEnGeneral,
} from "../../domain/compra/pedido";
import { formatoPrecio } from "../mapa/PanelTarifas";
import { COLOR } from "../mapa/estilos";

const RECARGA_MS = 30000;
const AVISO_MS = 4000;

const Leyenda = () => (
  <Box sx={{ display: "flex", gap: 2, fontSize: 12, color: COLOR.textoSuave, mt: 1 }}>
    {[
      ["Libre", "#ffffff", COLOR.acento],
      ["Elegido", COLOR.acento, COLOR.acento],
      ["Ocupado", COLOR.vendido, COLOR.vendido],
    ].map(([texto, fondo, borde]) => (
      <Box key={texto} sx={{ display: "flex", alignItems: "center", gap: 0.75 }}>
        <Box sx={{ width: 12, height: 12, borderRadius: "50%", bgcolor: fondo, border: `2px solid ${borde}` }} />
        {texto}
      </Box>
    ))}
  </Box>
);

// cajaConPartes: la caja de la ubicación con sus partes en otros lugares
const cajaConPartes = (sector) => {
  const r = ((sector.rotacion || 0) * Math.PI) / 180;
  const enPlano = (x, y) => [sector.x + x * Math.cos(r) - y * Math.sin(r), sector.y + x * Math.sin(r) + y * Math.cos(r)];
  const puntos = [];
  [{ x: 0, y: 0, ancho: sector.ancho, alto: sector.alto, rotacion: 0 }, ...(sector.partes || [])].forEach((p) => {
    const q = ((p.rotacion || 0) * Math.PI) / 180;
    [[0, 0], [p.ancho, 0], [0, p.alto], [p.ancho, p.alto]].forEach(([dx, dy]) =>
      puntos.push(enPlano(p.x + dx * Math.cos(q) - dy * Math.sin(q), p.y + dx * Math.sin(q) + dy * Math.cos(q)))
    );
  });
  const xs = puntos.map((p) => p[0]);
  const ys = puntos.map((p) => p[1]);
  const x = Math.min(...xs);
  const y = Math.min(...ys);
  return { x, y, ancho: Math.max(...xs) - x, alto: Math.max(...ys) - y };
};

// focoSuave: con el mapa del evento se abre cerca de la ubicación de la
// tarifa elegida, pero con bastante plano alrededor para moverse a otra.
const focoSuave = (mapa, sector) => {
  if (!sector?.en_mapa) return null;
  const caja = cajaConPartes(sector);
  const ancho = Math.min(mapa.ancho, Math.max(caja.ancho * 2.2, mapa.ancho * 0.5));
  const alto = Math.min(mapa.alto, Math.max(caja.alto * 2.2, mapa.alto * 0.5));
  const centroX = caja.x + caja.ancho / 2;
  const centroY = caja.y + caja.alto / 2;
  return {
    x: Math.min(Math.max(centroX - ancho / 2, 0), mapa.ancho - ancho),
    y: Math.min(Math.max(centroY - alto / 2, 0), mapa.alto - alto),
    ancho,
    alto,
  };
};

// alcance: qué ubicaciones se pueden tocar y qué tarifas maneja este modal. Por
// zona se elige sólo la tarifa desde la que se abrió; con el mapa del evento,
// cualquier lugar del plano, arrancando cerca de la ubicación de la tarifa.
const alcanceDe = (indice, tarifa) => {
  const sectores = indice.mapa.sectores.filter((s) => conLugares(s) && s.tarifas.length > 0);
  if (indice.mapa.modo === "mapa_evento") {
    const generales = indice.mapa.sectores.filter(esGeneralEnPlano);
    const tarifas = [...sectores, ...generales].flatMap((s) => s.tarifas);
    const adicionales = tarifas.flatMap((id) => indice.tarifas[id]?.adicionales.map((a) => a.idtarifa) || []);
    const propio = indice.sectores[tarifa.idubicacion];
    return {
      activos: new Set([...sectores, ...generales].map((s) => s.id)),
      editables: [...new Set([...tarifas, ...adicionales])],
      foco: focoSuave(indice.mapa, propio),
      resaltado: propio?.id ?? null,
    };
  }
  const sector = indice.sectores[tarifa.idubicacion];
  return {
    activos: new Set([tarifa.idubicacion]),
    editables: [tarifa.id, ...tarifa.adicionales.map((a) => a.idtarifa)],
    foco: sector && sector.en_mapa ? cajaConPartes(sector) : null,
  };
};

const soloEditables = (pedido, editables) => {
  const permitidas = new Set(editables);
  return {
    ...pedido,
    asientos: Object.fromEntries(Object.entries(pedido.asientos).filter(([, t]) => permitidas.has(t))),
    mesas: Object.fromEntries(Object.entries(pedido.mesas).filter(([, m]) => permitidas.has(m.idtarifa))),
    generales: Object.fromEntries(Object.entries(pedido.generales).filter(([t]) => permitidas.has(Number(t)))),
  };
};

// quitarOcupados saca del pedido lo que otra compra ocupó mientras se elegía
const quitarOcupados = (indice, pedido) => {
  let perdidos = 0;
  const asientos = {};
  Object.entries(pedido.asientos).forEach(([id, t]) => {
    const lugar = indice.asientos[id];
    if (lugar && !lugar.asiento.vendido) asientos[id] = t;
    else perdidos += 1;
  });
  const mesas = {};
  Object.entries(pedido.mesas).forEach(([id, m]) => {
    const mesa = indice.mesas[id]?.mesa;
    if (mesa && mesaLibre(mesa)) mesas[id] = m;
    else perdidos += 1;
  });
  return { pedido: { ...pedido, asientos, mesas }, perdidos };
};

const ElegirLugaresModal = ({ open, onClose, tarifa, cantidad, asientosSeleccionados, onAplicar }) => {
  const theme = useTheme();
  const celular = useMediaQuery(theme.breakpoints.down("md"));
  const { indice, recargar } = useMapaCompra();

  const tarifaMapa = indice && tarifa ? indice.tarifas[tarifa.id] : null;
  const alcance = useMemo(() => (indice && tarifaMapa ? alcanceDe(indice, tarifaMapa) : null), [indice, tarifaMapa]);

  const [pedido, setPedido] = useState(null);
  const [mesaAbierta, setMesaAbierta] = useState(null);
  const [tarifaPorSector, setTarifaPorSector] = useState({});
  const [sectorTocado, setSectorTocado] = useState(null);
  const [aviso, setAviso] = useState(null);
  const abierto = useRef(false);

  // al abrir: lo ya elegido y el mapa al día
  useEffect(() => {
    if (!open) {
      abierto.current = false;
      return undefined;
    }
    if (!abierto.current && indice && alcance) {
      abierto.current = true;
      setPedido(soloEditables(desdeSeleccion(indice, asientosSeleccionados, cantidad), alcance.editables));
      setMesaAbierta(null);
      setSectorTocado(null);
      setAviso(null);
      recargar();
    }
    const intervalo = setInterval(recargar, RECARGA_MS);
    return () => clearInterval(intervalo);
  }, [open, indice, alcance, asientosSeleccionados, cantidad, recargar]);

  useEffect(() => {
    if (!open || !pedido || !indice) return;
    const { pedido: limpio, perdidos } = quitarOcupados(indice, pedido);
    if (perdidos > 0) {
      setPedido(limpio);
      setAviso(perdidos === 1 ? "Un lugar que elegiste se acaba de ocupar." : `${perdidos} lugares que elegiste se acaban de ocupar.`);
    }
    // sólo cuando llega un mapa nuevo
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [indice]);

  useEffect(() => {
    if (!aviso) return undefined;
    const t = setTimeout(() => setAviso(null), AVISO_MS);
    return () => clearTimeout(t);
  }, [aviso]);

  if (!open || !indice || !tarifaMapa || !alcance || !pedido) return null;

  const modoEvento = indice.mapa.modo === "mapa_evento";
  const esMesaEntera = (sector) =>
    modoEvento ? Boolean(tarifaMesaEntera(indice, sector)) : sector.id === tarifaMapa.idubicacion && tarifaMapa.venta_mesa === MESA_ENTERA;

  const tarifaParaSector = (sector) => {
    if (!modoEvento) return tarifaMapa.venta_mesa === MESA_ENTERA ? null : tarifaMapa;
    const opciones = tarifasPorSilla(indice, sector);
    return opciones.find((t) => t.id === tarifaPorSector[sector.id]) || opciones[0] || null;
  };

  const elegirAsiento = (asiento, sector) => {
    setAviso(null);
    setSectorTocado(sector.id);
    setMesaAbierta(null);
    if (pedido.asientos[asiento.id]) {
      setPedido(alternarAsiento(pedido, asiento.id, pedido.asientos[asiento.id]));
      return;
    }
    const t = tarifaParaSector(sector);
    if (!t) {
      setAviso("En esta ubicación no hay entradas por lugar.");
      return;
    }
    if (asientosDeTarifa(pedido, t.id).length >= t.limite_persona) {
      setAviso(`Podés elegir hasta ${t.limite_persona} lugares de ${t.concepto}.`);
      return;
    }
    setPedido(alternarAsiento(pedido, asiento.id, t.id));
  };

  const elegirMesa = (mesa, sector) => {
    setAviso(null);
    setSectorTocado(sector.id);
    const t = modoEvento ? tarifaMesaEntera(indice, sector) : tarifaMapa;
    const actual = pedido.mesas[mesa.id];
    setMesaAbierta({
      mesa,
      tarifa: t,
      valor: {
        idpaquete: actual?.idpaquete ?? paquetesQueEntran(t, mesa)[0]?.id ?? null,
        adicionales: { ...(actual?.adicionales || {}) },
        sugeridas: { ...pedido.sugeridas },
      },
    });
  };

  // tocar una ubicación general suma una entrada; la cantidad se ajusta en "Tu selección"
  const tarifaGeneral = (sector) => {
    const opciones = tarifasGenerales(indice, sector);
    return opciones.find((t) => t.id === tarifaPorSector[sector.id]) || opciones[0] || null;
  };

  const cambiarGeneral = (t, cantidad) => {
    if (cantidad > t.limite_persona) {
      setAviso(`Podés elegir hasta ${t.limite_persona} de ${t.concepto}.`);
      return;
    }
    setPedido(ponerGeneral(pedido, t.id, cantidad));
  };

  const elegirGeneral = (sector) => {
    setAviso(null);
    setMesaAbierta(null);
    setSectorTocado(sector.id);
    const t = tarifaGeneral(sector);
    if (!t) {
      setAviso("Esta ubicación no tiene entradas a la venta.");
      return;
    }
    cambiarGeneral(t, (pedido.generales[t.id] || 0) + 1);
  };

  const confirmarMesa = () => {
    const { mesa, tarifa: t, valor } = mesaAbierta;
    let nuevo = ponerMesa(pedido, mesa.id, { idtarifa: t.id, idpaquete: valor.idpaquete, adicionales: valor.adicionales });
    nuevo = { ...nuevo, sugeridas: valor.sugeridas };
    setPedido(nuevo);
    setMesaAbierta(null);
  };

  const { renglones, total } = resumen(indice, pedido);
  const sectorConOpciones = sectorTocado && modoEvento ? indice.sectores[sectorTocado] : null;
  const opcionesSector = !sectorConOpciones
    ? []
    : conLugares(sectorConOpciones)
      ? tarifasPorSilla(indice, sectorConOpciones)
      : tarifasGenerales(indice, sectorConOpciones);
  const elegidaEnSector = (sector) => (conLugares(sector) ? tarifaParaSector(sector) : tarifaGeneral(sector));

  const confirmar = () => {
    onAplicar(aCambios(indice, pedido, alcance.editables));
    onClose();
  };

  const titulo = modoEvento ? "Elegí tus lugares" : tarifaMapa.concepto;
  const sectorTarifa = indice.sectores[tarifaMapa.idubicacion];
  const ayuda = esMesaEntera(sectorTarifa || { id: -1 })
    ? "Tocá una mesa libre para elegirla."
    : "Tocá los lugares libres para elegirlos.";

  return (
    <Dialog
      open={open}
      onClose={onClose}
      fullScreen={celular}
      maxWidth={false}
      PaperProps={{ sx: celular ? {} : { width: "min(1200px, 94vw)", height: "88vh", borderRadius: 3 } }}
    >
      <Box sx={{ display: "flex", flexDirection: "column", height: "100%" }}>
        <Box sx={{ display: "flex", alignItems: "center", px: 2.5, py: 1.5, borderBottom: `1px solid ${COLOR.borde}` }}>
          <Box sx={{ flex: 1, minWidth: 0 }}>
            <Typography sx={{ fontSize: 18, fontWeight: 700, color: COLOR.titulo }} noWrap>
              {titulo}
            </Typography>
            {!modoEvento && sectorTarifa && (
              <Typography sx={{ fontSize: 13, color: COLOR.textoSuave }}>{sectorTarifa.nombre}</Typography>
            )}
          </Box>
          <IconButton onClick={onClose} aria-label="Cerrar">
            <Close />
          </IconButton>
        </Box>

        <Box sx={{ flex: 1, minHeight: 0, display: "flex", flexDirection: celular ? "column" : "row" }}>
          <PlanoCompra
            mapa={indice.mapa}
            activos={alcance.activos}
            foco={alcance.foco}
            resaltado={modoEvento && sectorTocado ? sectorTocado : alcance.resaltado}
            estadoAsiento={(a) => (pedido.asientos[a.id] ? "elegido" : a.vendido ? "ocupado" : "libre")}
            esMesaEntera={esMesaEntera}
            estadoMesa={(m) => (pedido.mesas[m.id] ? "elegido" : mesaLibre(m) ? "libre" : "ocupado")}
            onAsiento={elegirAsiento}
            onMesa={elegirMesa}
            onGeneral={modoEvento ? elegirGeneral : null}
            cantidadGeneral={(s) => cantidadEnGeneral(indice, pedido, s)}
          />

          <Box
            sx={{
              width: celular ? "100%" : 360,
              maxHeight: celular ? "45vh" : "none",
              flexShrink: 0,
              display: "flex",
              flexDirection: "column",
              borderLeft: celular ? "none" : `1px solid ${COLOR.borde}`,
              borderTop: celular ? `1px solid ${COLOR.borde}` : "none",
            }}
          >
            <Box sx={{ flex: 1, overflowY: "auto", px: 2.5, py: 2 }}>
              {mesaAbierta ? (
                <PanelMesa
                  indice={indice}
                  mesa={mesaAbierta.mesa}
                  tarifa={mesaAbierta.tarifa}
                  valor={mesaAbierta.valor}
                  yaElegida={Boolean(pedido.mesas[mesaAbierta.mesa.id])}
                  onCambiar={(valor) => setMesaAbierta({ ...mesaAbierta, valor })}
                  onConfirmar={confirmarMesa}
                  onQuitar={() => {
                    setPedido(quitarMesa(pedido, mesaAbierta.mesa.id));
                    setMesaAbierta(null);
                  }}
                  onCancelar={() => setMesaAbierta(null)}
                />
              ) : (
                <>
                  <Typography sx={{ fontSize: 14, color: COLOR.texto }}>{ayuda}</Typography>
                  <Leyenda />

                  {opcionesSector.length > 1 && (
                    <Box sx={{ mt: 2 }}>
                      <Typography sx={{ fontSize: 13, fontWeight: 700, color: COLOR.titulo, mb: 0.75 }}>
                        Entrada para lo que elijas en {sectorConOpciones.nombre}
                      </Typography>
                      <ToggleButtonGroup
                        exclusive
                        size="small"
                        value={elegidaEnSector(sectorConOpciones)?.id}
                        onChange={(_, v) => v && setTarifaPorSector({ ...tarifaPorSector, [sectorConOpciones.id]: v })}
                        sx={{ flexWrap: "wrap", "& .MuiToggleButton-root": { textTransform: "none" } }}
                      >
                        {opcionesSector.map((t) => (
                          <ToggleButton key={t.id} value={t.id}>
                            {t.concepto} · {formatoPrecio(t.precio)}
                          </ToggleButton>
                        ))}
                      </ToggleButtonGroup>
                    </Box>
                  )}

                  {aviso && (
                    <Alert severity="warning" sx={{ mt: 2 }} onClose={() => setAviso(null)}>
                      {aviso}
                    </Alert>
                  )}

                  <Typography sx={{ fontSize: 13, fontWeight: 700, color: COLOR.titulo, mt: 2.5, mb: 0.5 }}>Tu selección</Typography>
                  {renglones.length === 0 ? (
                    <Typography sx={{ fontSize: 13, color: COLOR.textoSuave }}>Todavía no elegiste nada.</Typography>
                  ) : (
                    renglones.map((r) => (
                      <Box key={r.tarifa.id} sx={{ py: 1, borderBottom: `1px solid ${COLOR.borde}` }}>
                        <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", gap: 1 }}>
                          {pedido.generales[r.tarifa.id] > 0 ? (
                            <Box sx={{ display: "flex", alignItems: "center", gap: 0.5, minWidth: 0 }}>
                              <Cantidad
                                valor={pedido.generales[r.tarifa.id]}
                                maximo={r.tarifa.limite_persona}
                                onCambiar={(n) => cambiarGeneral(r.tarifa, n)}
                              />
                              <Typography noWrap sx={{ fontSize: 14, color: COLOR.titulo }}>{r.tarifa.concepto}</Typography>
                            </Box>
                          ) : (
                            <Typography sx={{ fontSize: 14, color: COLOR.titulo }}>
                              {r.cantidad} × {r.tarifa.concepto}
                            </Typography>
                          )}
                          <Typography sx={{ fontSize: 14, fontWeight: 600, flexShrink: 0 }}>{formatoPrecio(r.subtotal)}</Typography>
                        </Box>
                        {r.detalle.map((d, i) => (
                          <Typography key={i} sx={{ fontSize: 12, color: COLOR.textoSuave }}>
                            {d}
                          </Typography>
                        ))}
                      </Box>
                    ))
                  )}
                </>
              )}
            </Box>

            {!mesaAbierta && (
              <Box sx={{ px: 2.5, py: 1.5, borderTop: `1px solid ${COLOR.borde}` }}>
                <Box sx={{ display: "flex", justifyContent: "space-between", mb: 1 }}>
                  <Typography sx={{ fontSize: 13, color: COLOR.textoSuave }}>Total con servicio</Typography>
                  <Typography sx={{ fontSize: 16, fontWeight: 700, color: COLOR.titulo }}>{formatoPrecio(total)}</Typography>
                </Box>
                <Button
                  fullWidth
                  variant="contained"
                  onClick={confirmar}
                  sx={{ textTransform: "none", bgcolor: COLOR.acento, borderRadius: 2, py: 1.1, fontSize: 15 }}
                >
                  Confirmar
                </Button>
              </Box>
            )}
          </Box>
        </Box>
      </Box>
    </Dialog>
  );
};

export default ElegirLugaresModal;
